import PageHero from "@/components/PageHero";
import { Container, FadeIn, SectionLabel, SectionHeading, SectionDivider } from "@/components/Section";
import SEOHead from "@/components/SEOHead";
import NextStepsBlock from "@/components/NextStepsBlock";
import AcuityScheduleBlock from "@/components/AcuityScheduleBlock";
import PartnersStrip from "@/components/PartnersStrip";
import SocialEcosystem from "@/components/SocialEcosystem";
import StayConnected from "@/components/StayConnected";
import { IMG } from "@/lib/data";
import { Sparkles, Heart, Brain, Wand2, Accessibility, ArrowRight } from "lucide-react";

export default function InteractivePlayground() {
  return (
    <div data-testid="interactive-playground-page" className="bg-[#050505]">
      <SEOHead
        cmsPage="interactive-playground"
        title="Interactive Playground · Sensory-Aware Immersive Play for Under-12s · Temple of Kulture"
        description="Projection mapping, AR sandbox play, gentle sensory experiences for under-12s and their families. Medway · Kent · London. Funded places, accessibility-first, parent-supervised."
        image={IMG.dance2 || IMG.heroPortal}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Event",
          name: "Temple of Kulture — Interactive Playground",
          description: "Immersive projection and sensory play sessions for under-12s.",
          audience: { "@type": "PeopleAudience", suggestedMinAge: 3, suggestedMaxAge: 12 },
          organizer: { "@type": "Organization", name: "Temple of Kulture" },
        }}
      />
      <PageHero
        cmsPage="interactive-playground"
        eyebrow="Under 16"
        title="Play, like you remember."
        kicker="Interactive Playground"
        copy="Projection floors, AR sandboxes, soft sensory rooms and immersive movement — designed for under-12s and the grown-ups who care for them."
        bg={IMG.dance2 || IMG.heroPortal}
        focalMobile="50% 28%"
        focalDesktop="50% 40%"
        primaryCta={{ to: "/whats-on", label: "Upcoming sessions" }}
        secondaryCta={{ to: "/join", label: "Join the family list" }}
      />

      <section className="py-24 sm:py-28 bg-zinc-950 border-y border-amber-500/10">
        <Container>
          <div className="grid lg:grid-cols-12 gap-12">
            <FadeIn className="lg:col-span-7">
              <SectionLabel>Why we built this</SectionLabel>
              <SectionHeading className="mt-5">
                Childhood, <span className="text-amber-400">re-imagined.</span>
              </SectionHeading>
              <p className="mt-6 text-zinc-300 text-lg leading-relaxed">
                The under-12 years are the most imaginative of any human life
                — and the least served by immersive cultural spaces. Our
                Interactive Playground is a 90-minute window where children
                step into projection environments, paint with light, build
                with shadow, and move with stories projected around them.
              </p>
              <p className="mt-4 text-zinc-300 leading-relaxed">
                Sessions are <strong>sensory-aware</strong> by default:
                gentle soundscapes, quieter zones, breakable from anywhere,
                run by DBS-checked artists and educators.
              </p>
            </FadeIn>
            <FadeIn delay={0.1} className="lg:col-span-5 space-y-4">
              <Feat Icon={Wand2} title="Projection-floor play" desc="Step on lights, paint with shadow, dance with stories." />
              <Feat Icon={Brain} title="Sensory-aware design" desc="Quieter zones, dimmable sound, breakable any time." />
              <Feat Icon={Accessibility} title="Access-first" desc="Wheelchair-accessible, parent-supported, BSL on request." />
              <Feat Icon={Heart} title="Funded places" desc="Free or subsidised places for families on lower incomes." />
            </FadeIn>
          </div>
        </Container>
      </section>

      <SectionDivider label="Book a session" />
      <Container className="pb-20">
        <div className="grid md:grid-cols-2 gap-4">
          <AcuityScheduleBlock
            eyebrow="Family taster"
            title="60-minute Interactive Playground session"
            copy="One adult + up to two under-12s. Free to low-cost depending on session."
            slug="interactive-playground"
            audience="Under 12 + adult"
            duration="60 mins"
            fallbackTo="/join"
            fallbackLabel="Join waitlist"
            testId="acuity-ip-taster"
          />
          <AcuityScheduleBlock
            eyebrow="Schools & SEND"
            title="In-school visits & SEND-friendly sessions"
            copy="Bespoke half-day for primary schools, SEND units and alternative provision."
            slug="schools-and-send"
            audience="Schools / SEND"
            duration="Half-day"
            fallbackTo="/community"
            fallbackLabel="Talk to us"
            testId="acuity-ip-schools"
          />
        </div>
      </Container>

      <SocialEcosystem
        eyebrow="See it in action"
        title="Inside the Playground"
        copy="Short clips and parent reactions — across TikTok, Instagram, YouTube and Twitch."
      />

      <section className="py-20 sm:py-24 bg-zinc-950 border-t border-amber-500/10">
        <PartnersStrip
          eyebrow="In partnership with"
          title="Funded by real councils, real youth networks"
          copy="Interactive Playground sessions run alongside Medway Council, Kent County Council, Medway Youth and Arts Council England funding."
          programmeKey="community"
          variant="card"
          testId="ip-partners"
        />
      </section>

      <NextStepsBlock
        title="Continue the journey"
        eyebrow="Where to next"
        steps={[
          { label: "Older sibling", title: "After School Clubs (7–16)", desc: "Weekly creative-tech clubs.", to: "/after-school-clubs", cta: "Discover", testid: "ip-next-asc" },
          { label: "Home learning", title: "Home Education programme", desc: "Wed/Thu sessions for global majority families.", to: "/home-education", cta: "Learn more", testid: "ip-next-home-ed" },
          { label: "Stay close", title: "Join the Temple", desc: "Family-list updates straight to your inbox.", to: "/join", cta: "Sign up", testid: "ip-next-join" },
        ]}
      />

      {/* WhatsApp opt-in — parent updates, family events */}
      <StayConnected
        variant="whatsapp"
        pageSource="interactive-playground"
        testid="ip-stay"
        whatsappHeadline="STAY CLOSE TO THE PLAYGROUND"
        whatsappTagline="Family event drops, session releases and free taster days — the moment they're announced."
        whatsappCta="Join the Channel"
        bullets={[
          "Family session releases",
          "Free taster days and open studios",
          "Half-term workshops and play labs",
          "Sensory-aware family events",
          "Parent updates & community calls",
        ]}
      />
    </div>
  );
}

function Feat({ Icon, title, desc }) {
  return (
    <div className="border border-white/5 bg-black p-5">
      <Icon className="h-5 w-5 text-amber-400" />
      <p className="mt-3 font-display font-bold text-sm uppercase tracking-[0.15em]">{title}</p>
      <p className="mt-1 text-sm text-zinc-400 leading-relaxed">{desc}</p>
    </div>
  );
}
